import type { ReactElement } from 'react';
import type { GameRequestProps, SearchViewProps } from '../types';
import type { GetStaticProps, GetStaticPropsContext } from 'next';

import * as React from 'react';

import { View } from '../generated/search';
// import { SearchA as View } from '../containers/SearchA';
import { BaseLayout } from '../layouts/BaseLayout';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { cleanSlug, getLocalizedPaths, getLocalizedSlugs } from '../utils/localize-helpers';
import { translateUrl } from 'next-translate-routes';
import { getPopularGames, getSearchResults } from '../app/api';
import { mappingGame } from '../app/mappings';
import { getNonPageNamespaces } from '../utils/get-non-page-namespaces';

export default function SearchPage(props: SearchViewProps) {
  const { pageContext } = props;
  const { t, i18n } = useTranslation(['common', 'search']);
  const router = useRouter();
  const paths = getLocalizedPaths(router);
  const slugs = getLocalizedSlugs(router);
  const pageContextWithPaths = { ...pageContext, paths, slugs, slug: cleanSlug(router.pathname) };

  return (
    <>
      <NextSeo
        title={
          i18n.exists('search:title', { fallbackLng: pageContextWithPaths.locale })
            ? t('search:title')
            : t('common:title')
        }
        description={
          i18n.exists('search:description', { fallbackLng: pageContextWithPaths.locale })
            ? t('search:description')
            : t('common:description')
        }
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View
        {...props}
        title={
          i18n.exists('search:headline', { fallbackLng: pageContextWithPaths.locale })
            ? t('search:headline')
            : t('common:headline')
        }
        body={
          i18n.exists('search:body', { fallbackLng: pageContextWithPaths.locale }) ? t('search:body') : t('common:body')
        }
        pageContext={pageContextWithPaths}
      />
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
SearchPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType="search" pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  // Todo: HUZ2ERA-1115 - Query will return undefined due to us using static site generation. There seems to be
  //       a catch-all solution, but haven't had the time to try it out:
  //       https://medium.com/@igorbadanuk/next-js-advanced-ssg-with-path-query-2b984e713c3f
  //       Problem right now is that we can't fetch the search results, so our Next app will
  //       just generate a list of popular games. And we will have to handle the search request on client side.
  //       Or maybe just use server side rendering instead?
  const params: string = (context?.params?.q || '') as string;
  const keyword = params.replace(/[^\s\w]/gi, '');
  const locale: string = (context.locale || context.defaultLocale) as string;
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;

  let games: GameRequestProps[] | undefined = await getSearchResults(
    keyword,
    {
      _limit: 18,
    },
    {
      ssr: true,
      locale,
    }
  );

  // Make sure we always return something.
  if (Array.isArray(games) && games.length <= 0) {
    games = await getPopularGames(
      {
        period: 'week',
        _limit: 18,
        _gameRandom: true,
        _fieldsGame: 'image', // Include "image" property for each game.
      },
      {
        ssr: true,
        locale,
      }
    );
  }

  if (!games) return { notFound: true };

  const page = {
    title: null,
    body: null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    slugs: [],
    slug: 'search',
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/search', locale)}`,
        path: `${translateUrl('/search', locale)}`,
        name: 'Search', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
    ],
  };

  return {
    props: {
      title,
      body,
      games: games.map((game: GameRequestProps) => ({
        ...mappingGame(game),
      })),
      global: {
        popular: [],
        latest: [],
      },
      pageContext,
      // Get our translations by namespace and locale.
      ...(await serverSideTranslations(locale as string, ['common', 'search', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
